import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Modal, Spinner } from "react-bootstrap";
import { getJobServicesByJob } from "requesters/jobServiceRequester";
import { getBaseUrl } from "../../../reducers";
import { FIELD_WORK_STATES } from "utils/constants";

/**
 * A modal that shows all FieldWork-state job services linked to a job,
 * allowing the user to select which ones to cancel or put on hold.
 *
 * Props:
 *  - show: boolean
 *  - onHide: () => void
 *  - job: object (the job)
 *  - jobService: object (the current job service)
 *  - actionType: "cancel" | "on-hold" | "client-delayed"
 *  - onConfirm: (selectedJobServiceIds: string[]) => void
 */
const JobServiceSelectionModal = ({
  show,
  onHide,
  job,
  jobService,
  actionType,
  onConfirm,
}) => {
  const baseUrl = useSelector(getBaseUrl);
  const [fieldWorkJobServices, setFieldWorkJobServices] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const currentJsId = jobService?.id;
  const jobNo = job?.attributes?.jobNo;

  // Fetch all job services for the job when modal opens
  useEffect(() => {
    if (!show || !job?.id) return;

    setFetching(true);
    getJobServicesByJob(baseUrl, job.id)
      .then((res) => {
        if (res.status === 200) {
          const allJobServices = Object.values(
            _.get(res.response, "jobService", {})
          );
          // Filter to only FieldWork states, excluding the current job service
          // (the current one is always shown separately as locked)
          const otherFieldWorkJs = allJobServices.filter(
            (js) =>
              FIELD_WORK_STATES.includes(js.attributes?.state) &&
              String(js.id) !== String(currentJsId)
          );
          setFieldWorkJobServices(otherFieldWorkJs);

          // Default: only the current job service is selected (locked)
          // No optional ones selected by default
          setSelectedIds(new Set());
        }
        setFetching(false);
      })
      .catch(() => {
        setFetching(false);
      });
  }, [show, job?.id, baseUrl, currentJsId]);

  // Whether all *optional* (non-current) items are selected
  const allSelected = useMemo(
    () =>
      fieldWorkJobServices.length > 0 &&
      fieldWorkJobServices.every((js) => selectedIds.has(String(js.id))),
    [fieldWorkJobServices, selectedIds]
  );

  const handleToggle = (jsId) => {
    // Never allow toggling the current job service
    if (String(jsId) === String(currentJsId)) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(String(jsId))) {
        next.delete(String(jsId));
      } else {
        next.add(String(jsId));
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (allSelected) {
      // Unselect all optional items (current JS stays selected via lock)
      setSelectedIds(new Set());
    } else {
      // Select all optional items
      const allIds = new Set(fieldWorkJobServices.map((js) => String(js.id)));
      setSelectedIds(allIds);
    }
  };

  const handleConfirm = () => {
    setLoading(true);
    // Always include the current job service + any selected optional ones
    const allSelectedIds = [String(currentJsId), ...Array.from(selectedIds)];
    onConfirm(allSelectedIds);
  };

  // Reset loading when modal closes
  useEffect(() => {
    if (!show) {
      setLoading(false);
    }
  }, [show]);

  const actionLabel = useMemo(() => {
    switch (actionType) {
      case "cancel":
        return "Cancel";
      case "on-hold":
        return "Put On Hold";
      case "client-delayed":
        return "Client Delay";
      default:
        return "Proceed";
    }
  }, [actionType]);

  const modalTitle = useMemo(() => {
    switch (actionType) {
      case "cancel":
        return "Cancel Job Services";
      case "on-hold":
        return "Put Job Services On Hold";
      case "client-delayed":
        return "Client Delayed Job Services";
      default:
        return "Select Job Services";
    }
  }, [actionType]);

  const handleJobServiceClick = (jsId) => {
    const js = fieldWorkJobServices.find((j) => String(j.id) === String(jsId));
    if (js) {
      const jobId = js.attributes?.jobId || job?.id;
      window.open(`/jobs/${jobId}/job_services/${js.id}`, "_blank");
    } else {
      // Current job service (not in the optional list)
      window.open(`/jobs/${job?.id}/job_services/${jsId}`, "_blank");
    }
  };

  const stateLabel = (state) => {
    if (!state) return "";
    return state
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <h2 className="modal-title">{modalTitle}</h2>
          {jobNo && (
            <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>
              Job No: <strong>{jobNo}</strong>
            </p>
          )}
        </Modal.Title>
      </Modal.Header>
      <div className="modal-body px-md-5">
        {fetching ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" size="sm" />
            <span className="ml-2">Loading job services...</span>
          </div>
        ) : (
          <>
            <p>
              Select which job services you want to{" "}
              <strong>{actionLabel.toLowerCase()}</strong>:
            </p>

            {/* Select All / Unselect All — only shown when there are optional items */}
            {fieldWorkJobServices.length > 0 && (
              <>
                <div className="mb-3 d-flex align-items-center">
                  <label
                    className="cursor-pointer mb-0 d-flex align-items-center"
                    style={{ userSelect: "none" }}
                  >
                    <input
                      type="checkbox"
                      className="mr-2"
                      style={{ width: "18px", height: "18px" }}
                      checked={allSelected}
                      onChange={handleSelectAll}
                    />
                    <strong>
                      {allSelected ? "Unselect All" : "Select All"}
                    </strong>
                  </label>
                </div>
                <hr className="mt-0" />
              </>
            )}

            {/* Job Service List */}
            <div
              style={{
                maxHeight: "350px",
                overflowY: "auto",
              }}
            >
              {/* Current job service — always shown, always selected, locked */}
              {jobService && (
                <div
                  className="d-flex align-items-center p-2 mb-1 rounded bg-light border"
                  style={{ cursor: "default" }}
                >
                  <input
                    type="checkbox"
                    className="mr-3"
                    style={{ width: "18px", height: "18px", flexShrink: 0 }}
                    checked
                    disabled
                  />
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJobServiceClick(currentJsId);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#007bff",
                          cursor: "pointer",
                          padding: 0,
                          textDecoration: "underline",
                          textAlign: "left",
                          fontWeight: "600",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = "#0056b3";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = "#007bff";
                        }}
                      >
                        {jobService.attributes?.code || ""}
                      </button>
                      <span
                        className="badge badge-info ml-2"
                        style={{ fontSize: "11px" }}
                      >
                        Current
                      </span>
                    </div>
                    {jobService.attributes?.label &&
                      jobService.attributes.label !== jobService.attributes?.code && (
                        <small className="text-muted d-block">
                          {jobService.attributes.label}
                        </small>
                      )}
                  </div>
                  <div className="text-right">
                    <span
                      className="badge badge-secondary"
                      style={{ fontSize: "11px" }}
                    >
                      {stateLabel(jobService.attributes?.state || "")}
                    </span>
                  </div>
                </div>
              )}

              {/* Other FieldWork-state job services — selectable */}
              {fieldWorkJobServices.map((js) => {
                const jsCode = js.attributes?.code || "";
                const jsLabel = js.attributes?.label || jsCode;
                const jsState = js.attributes?.state || "";

                return (
                  <div
                    key={js.id}
                    className="d-flex align-items-center p-2 mb-1 rounded"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleToggle(js.id)}
                  >
                    <input
                      type="checkbox"
                      className="mr-3"
                      style={{ width: "18px", height: "18px", flexShrink: 0 }}
                      checked={selectedIds.has(String(js.id))}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggle(js.id)}
                    />
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleJobServiceClick(js.id);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#007bff",
                            cursor: "pointer",
                            padding: 0,
                            textDecoration: "underline",
                            textAlign: "left",
                            fontWeight: "600",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = "#0056b3";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = "#007bff";
                          }}
                        >
                          {jsCode}
                        </button>
                      </div>
                      {jsLabel !== jsCode && (
                        <small className="text-muted d-block">{jsLabel}</small>
                      )}
                    </div>
                    <div className="text-right">
                      <span
                        className="badge badge-secondary"
                        style={{ fontSize: "11px" }}
                      >
                        {stateLabel(jsState)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="modal-footer d-flex justify-content-end p-4">
        <button className="btn btn-link btn-lg" onClick={onHide} type="button">
          Close
        </button>
        <button
          className="btn btn-red btn-lg"
          onClick={handleConfirm}
          disabled={loading || fetching}
          type="button"
        >
          {actionLabel} ({selectedIds.size + 1})
          {loading && (
            <Spinner className="ml-1" animation="border" role="status" size="sm" />
          )}
        </button>
      </div>
    </Modal>
  );
};

export default JobServiceSelectionModal;
